import { apiGet, apiPost } from './client';

// ─── Types ───────────────────────────────────────────────────────────────

export interface SgpFinancialResult {
  success: boolean;
  protocol: string | null;
  contractId: string | null;
  message: string | null;
}

export interface SgpSecondCopyResult extends SgpFinancialResult {
  invoices: Array<{
    invoiceId: string | null;
    documentNumber: string | null;
    dueDate: string | null;
    originalDueDate: string | null;
    amount: number | null;
    originalAmount: number | null;
    digitableLine: string | null;
    paymentUrl: string | null;
  }>;
}

export interface SgpPixResult {
  success: boolean;
  invoiceId: string;
  protocol: string | null;
  message: string | null;
  copyPasteCode: string | null;
  qrCode: string | null;
  expiresAt: string | null;
}

export interface SgpSendInvoiceResult extends SgpFinancialResult {
  occurrenceMayHaveBeenCreated: boolean;
}

export interface SgpSensitiveResult {
  ok: boolean;
  data?: unknown;
  error?: string;
}

export interface SgpRadiusSessionResult {
  online: boolean | null;
  username: string | null;
  nas: string | null;
  framedIpAddress: string | null;
  callingStationId: string | null;
  sessionSeconds: number | null;
  sessionTime: string | null;
  startTime: string | null;
  stopTime: string | null;
  inputOctets: number | null;
  outputOctets: number | null;
}

// ─── Authorization Helpers ──────────────────────────────────────────────

export async function requestSgpAuthorization(data: {
  operationId: string;
  companyId?: string;
  conversationId?: string;
  customerId?: string;
  payload: Record<string, unknown>;
  reason: string;
}): Promise<{ id: string }> {
  return apiPost<{ id: string }>('/integrations/sgp/authorizations', data);
}

export async function approveSgpAuthorization(id: string, reason?: string): Promise<void> {
  await apiPost(`/integrations/sgp/authorizations/${id}/approve`, { reason });
}

export async function rejectSgpAuthorization(id: string, reason: string): Promise<void> {
  await apiPost(`/integrations/sgp/authorizations/${id}/reject`, { reason });
}

export async function listSgpAuthorizations(companyId?: string): Promise<unknown[]> {
  const params = companyId ? `?companyId=${companyId}` : '';
  return apiGet<unknown[]>(`/integrations/sgp/authorizations${params}`);
}

export async function listSgpOperations(): Promise<unknown[]> {
  return apiGet<unknown[]>('/integrations/sgp/operations');
}

export async function getSgpPreRegistrationOptions(companyId?: string): Promise<{
  dueDates: Array<{ id: string; label: string }>;
  sellers: Array<{ id: string; label: string }>;
  billingMethods: Array<{ id: string; label: string }>;
}> {
  return apiGet(`/integrations/sgp/precadastro/options${companyId ? `?companyId=${encodeURIComponent(companyId)}` : ''}`);
}

// ─── Safe Financial Operations ──────────────────────────────────────────

export async function sgpInvoiceSecondCopy(dto: {
  contractId: string;
  documentNumber: string;
  companyId?: string;
  authorizationId?: string;
}): Promise<SgpSecondCopyResult> {
  return apiPost<SgpSecondCopyResult>('/integrations/sgp/finance/invoices/second-copy', dto);
}

export async function sgpInvoicePix(dto: {
  contractId: string;
  invoiceId: string;
  companyId?: string;
  authorizationId?: string;
}): Promise<SgpPixResult> {
  return apiPost<SgpPixResult>('/integrations/sgp/finance/invoices/pix', dto);
}

export async function sgpInvoiceSend(dto: {
  contractId: string;
  documentNumber: string;
  channel: 'email' | 'sms';
  companyId?: string;
  authorizationId?: string;
}): Promise<SgpSendInvoiceResult> {
  return apiPost<SgpSendInvoiceResult>('/integrations/sgp/finance/invoices/send', dto);
}

// ─── Sensitive Write Operations ─────────────────────────────────────────

export async function sgpCreateCustomer(dto: {
  planId: number;
  dueDay: number;
  billingMethodCode: number;
  login: string;
  password: string;
  email?: string;
  popId?: string;
  observation?: string;
  nas?: string;
  centralLogin?: string;
  centralPassword?: string;
  ip?: string;
  mac?: string;
  serviceDescription?: string;
  generateOs?: boolean;
  installationPrice?: number;
  installationDiscount?: number;
  installationDownPayment?: number;
  content?: string;
  companyId?: string;
  authorizationId?: string;
}): Promise<SgpSensitiveResult> {
  return apiPost<SgpSensitiveResult>('/integrations/sgp/customer/create', dto);
}

export async function sgpCreateTicket(dto: {
  contractId: string;
  content: string;
  occurrenceTypeId: number;
  occurrenceMethodId?: number;
  sectorId?: number;
  technicianId?: number;
  companyId?: string;
  authorizationId?: string;
}): Promise<SgpSensitiveResult> {
  return apiPost<SgpSensitiveResult>('/integrations/sgp/tickets/create', dto);
}

export async function sgpRebootCpe(dto: {
  serviceId: string;
  companyId?: string;
  authorizationId?: string;
}): Promise<SgpSensitiveResult> {
  return apiPost<SgpSensitiveResult>('/integrations/sgp/cpe/reboot', dto);
}

export async function sgpDisconnectRadius(dto: {
  username: string;
  nas?: string;
  nasPortId?: string;
  companyId?: string;
  authorizationId?: string;
}): Promise<SgpSensitiveResult> {
  return apiPost<SgpSensitiveResult>('/integrations/sgp/radius/disconnect', dto);
}

// ─── Critical Write Operations (need pre-approved authorization) ────────

export async function sgpSettleInvoice(dto: {
  invoiceId: string;
  paymentDate: string;
  amount: number;
  authorizationId: string;
  companyId?: string;
}): Promise<SgpSensitiveResult> {
  return apiPost<SgpSensitiveResult>('/integrations/sgp/finance/invoices/settle', dto);
}

export async function sgpReverseInvoice(dto: {
  invoiceId: string;
  reason: string;
  authorizationId: string;
  companyId?: string;
}): Promise<SgpSensitiveResult> {
  return apiPost<SgpSensitiveResult>('/integrations/sgp/finance/invoices/reverse', dto);
}

export async function sgpUpdateContractStatus(dto: {
  contractId: string;
  statusId: number;
  reason: string;
  authorizationId: string;
  companyId?: string;
}): Promise<SgpSensitiveResult> {
  return apiPost<SgpSensitiveResult>('/integrations/sgp/contracts/status', dto);
}

// ─── Destructive Operations ─────────────────────────────────────────────

export async function sgpCancelInvoice(dto: {
  invoiceId: string;
  reason: string;
  authorizationId: string;
  companyId?: string;
}): Promise<SgpSensitiveResult> {
  return apiPost<SgpSensitiveResult>('/integrations/sgp/finance/invoices/cancel', dto);
}

export async function sgpDeleteCard(dto: {
  cardId: string;
  authorizationId: string;
  companyId?: string;
}): Promise<SgpSensitiveResult> {
  return apiPost<SgpSensitiveResult>('/integrations/sgp/cards/delete', dto);
}

export async function sgpDeleteRecurringCharge(dto: {
  cardId: string;
  reason: string;
  authorizationId: string;
  companyId?: string;
}): Promise<SgpSensitiveResult> {
  return apiPost<SgpSensitiveResult>('/integrations/sgp/recurring-charges/delete', dto);
}

export async function sgpAuthorizeOnu(dto: {
  oltId: string;
  ponId: string;
  onuSerial: string;
  serviceId?: string;
  templateId?: string;
  description?: string;
  authorizationId: string;
  companyId?: string;
}): Promise<SgpSensitiveResult> {
  return apiPost<SgpSensitiveResult>('/integrations/sgp/ftth/onu/authorize', dto);
}

export async function sgpDeauthorizeOnu(dto: {
  onuId: string;
  reason: string;
  authorizationId: string;
  companyId?: string;
}): Promise<SgpSensitiveResult> {
  return apiPost<SgpSensitiveResult>('/integrations/sgp/ftth/onu/deauthorize', dto);
}

export async function sgpReverseStock(dto: {
  stockItemId: string;
  reason: string;
  quantity?: number;
  reversalDate?: string;
  authorizationId: string;
  companyId?: string;
}): Promise<SgpSensitiveResult> {
  return apiPost<SgpSensitiveResult>('/integrations/sgp/stock/reversal', dto);
}

export async function sgpDeleteGenericService(dto: {
  serviceId: string;
  reason: string;
  authorizationId: string;
  companyId?: string;
}): Promise<SgpSensitiveResult> {
  return apiPost<SgpSensitiveResult>('/integrations/sgp/services/delete', dto);
}

export async function sgpDeleteCustomerDocument(dto: {
  documentId: string;
  reason: string;
  authorizationId: string;
  companyId?: string;
}): Promise<SgpSensitiveResult> {
  return apiPost<SgpSensitiveResult>('/integrations/sgp/customer-documents/delete', dto);
}

// ─── Diagnostics ────────────────────────────────────────────────────────

export async function testSgpCredential(companyId?: string, document?: string): Promise<Record<string, unknown>> {
  return apiPost<Record<string, unknown>>('/integrations/sgp/test', { companyId, document });
}

export async function testSgpReadOnlyFlow(companyId?: string, document?: string): Promise<Record<string, unknown>> {
  return apiPost<Record<string, unknown>>('/integrations/sgp/read-only-diagnostics', { companyId, document });
}

export async function testRadius(username: string, companyId?: string, document?: string): Promise<Record<string, unknown>> {
  return apiPost<Record<string, unknown>>('/integrations/sgp/radius-test', { companyId, username, document });
}

// ─── 360° View ────────────────────────────────────────────────────────

export interface Sgp360View {
  customer: { id?: string; code?: string; name?: string; document?: string; status?: string; registrationDate?: string } | null;
  contracts: Array<{ id: string; code?: string; status?: string; planName?: string; planId?: number; services?: unknown[] }>;
  services: Array<{ id: string; type?: string; name?: string; status?: string; serviceId?: string }>;
  invoices: unknown[];
  tickets: unknown[];
}

export async function getSgp360View(customerId: string): Promise<Sgp360View> {
  return apiGet<Sgp360View>(`/customers/${customerId}/sgp-client-360`);
}
